import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Trash2, UserPlus } from 'lucide-react';
import { workspacesApi } from '@/api/workspaces';
import { extractApiError } from '@/api/client';
import TotpModal from '@/components/auth/TotpModal';
import UserPicker, { type PickedUser } from '@/components/ui/UserPicker';

/**
 * Phase 12.1 — workspace branding editor + members CRUD.
 *
 * Reachable via /admin/workspaces/:slug. Same TOTP retry pattern as
 * AdminWorkspaces — runWithTotp wraps each sensitive call.
 */
export default function AdminWorkspaceDetail() {
  const { slug = '' } = useParams<{ slug: string }>();
  const qc = useQueryClient();

  const { data: ws, isLoading } = useQuery({
    queryKey: ['admin-workspace', slug],
    queryFn: () => workspacesApi.get(slug),
    enabled: !!slug,
  });

  const { data: members } = useQuery({
    queryKey: ['admin-workspace-members', slug],
    queryFn: () => workspacesApi.getMembers(slug),
    enabled: !!slug,
  });

  // Branding form local state; reset on data change.
  const [displayName, setDisplayName] = useState('');
  const [brandColor, setBrandColor] = useState('#7c3aed');
  const [iconUrl, setIconUrl] = useState('');
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ws) return;
    setDisplayName(ws.displayName);
    setBrandColor(ws.brandColor ?? '#7c3aed');
    setIconUrl(ws.iconUrl ?? '');
  }, [ws]);

  // ── TOTP shared state (same pattern as AdminWorkspaces) ──
  const [totpOpen, setTotpOpen] = useState(false);
  const [totpError, setTotpError] = useState<{
    code: 'TOTP_REQUIRED' | 'TOTP_CODE_REQUIRED' | 'TOTP_INVALID' | null;
    message: string | null;
  }>({ code: null, message: null });
  const [totpLoading, setTotpLoading] = useState(false);
  const pendingOpRef = useState<{ run: (code?: string) => Promise<void> } | null>(null);

  async function runWithTotp(op: (code?: string) => Promise<unknown>): Promise<void> {
    pendingOpRef[1]({ run: async (code) => { await op(code); } });
    try {
      await op();
      pendingOpRef[1](null);
    } catch (e) {
      const apiErr = extractApiError(e);
      if (apiErr.code === 'TOTP_REQUIRED') {
        setTotpError({ code: 'TOTP_REQUIRED', message: apiErr.message });
        setTotpOpen(true);
        return;
      }
      if (apiErr.code === 'TOTP_CODE_REQUIRED') {
        setTotpError({ code: 'TOTP_CODE_REQUIRED', message: null });
        setTotpOpen(true);
        return;
      }
      pendingOpRef[1](null);
      throw e;
    }
  }

  async function handleTotpSubmit(code: string) {
    const pending = pendingOpRef[0];
    if (!pending) { setTotpOpen(false); return; }
    setTotpLoading(true);
    try {
      await pending.run(code);
      setTotpOpen(false);
      setTotpError({ code: null, message: null });
      pendingOpRef[1](null);
    } catch (e) {
      const apiErr = extractApiError(e);
      if (apiErr.code === 'TOTP_INVALID') {
        setTotpError({ code: 'TOTP_INVALID', message: apiErr.message });
      } else {
        setTotpError({ code: null, message: apiErr.message });
      }
    } finally {
      setTotpLoading(false);
    }
  }

  // ── Branding save ──
  const onSaveBranding = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null); setSavedMsg(null);
    try {
      await runWithTotp(async (code) => {
        await workspacesApi.update(slug, {
          displayName: displayName.trim(),
          brandColor: brandColor || null,
          iconUrl: iconUrl.trim() || null,
        }, code);
        qc.invalidateQueries({ queryKey: ['admin-workspace', slug] });
        qc.invalidateQueries({ queryKey: ['admin-workspaces'] });
        setSavedMsg('Сохранено');
        setTimeout(() => setSavedMsg(null), 2500);
      });
    } catch (e2) {
      setError(extractApiError(e2).message);
    }
  };

  // ── Add member ──
  // Phase 13 — picker вместо ID-копи-паста.
  const [newMemberPicked, setNewMemberPicked] = useState<PickedUser | null>(null);
  const [newMemberRole, setNewMemberRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');
  const [memberError, setMemberError] = useState<string | null>(null);

  // Уже добавленные members исключаем из автокомплита.
  const existingMemberIds = (members ?? []).map((m) => m.userId);

  const onAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setMemberError(null);
    if (!newMemberPicked) { setMemberError('Выберите пользователя'); return; }
    try {
      await runWithTotp(async (code) => {
        await workspacesApi.addMember(slug, newMemberPicked.id, newMemberRole, code);
        qc.invalidateQueries({ queryKey: ['admin-workspace-members', slug] });
        setNewMemberPicked(null);
      });
    } catch (e2) {
      setMemberError(extractApiError(e2).message);
    }
  };

  const onRemoveMember = async (userId: string, username?: string) => {
    if (!confirm(`Удалить ${username ?? userId} из workspace?`)) return;
    try {
      await runWithTotp(async (code) => {
        await workspacesApi.removeMember(slug, userId, code);
        qc.invalidateQueries({ queryKey: ['admin-workspace-members', slug] });
      });
    } catch (e2) {
      setMemberError(extractApiError(e2).message);
    }
  };

  if (isLoading) return <div className="p-6 text-surface-500">Загрузка…</div>;
  if (!ws) return <div className="p-6 text-surface-500">Workspace не найден.</div>;

  return (
    <div className="p-6 max-w-3xl space-y-6">
      <Link to="/admin/workspaces" className="btn-ghost text-xs inline-flex items-center gap-1">
        <ArrowLeft size={13} /> к списку
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-surface-50">{ws.displayName}</h1>
        <p className="text-xs text-surface-500 mt-1 font-mono">{ws.slug}.{import.meta.env.VITE_MAIN_DOMAIN ?? 'chat56.ru'}</p>
        {ws.archived && (
          <p className="mt-2 inline-block px-2 py-0.5 rounded text-xs bg-amber-900/40 text-amber-300">
            archived
          </p>
        )}
      </div>

      {/* Branding ── */}
      <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-surface-200 mb-4">Брендинг</h2>
        <form onSubmit={onSaveBranding} className="space-y-4">
          {error && (
            <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          {savedMsg && (
            <div className="bg-emerald-900/40 border border-emerald-700 text-emerald-300 text-sm rounded-lg px-3 py-2">
              {savedMsg}
            </div>
          )}
          <div>
            <label className="block text-surface-300 text-xs font-medium mb-1.5">Название</label>
            <input
              value={displayName} onChange={(e) => setDisplayName(e.target.value)}
              className="irc-input" maxLength={64}
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0">
              <label className="block text-surface-300 text-xs font-medium mb-1.5">Цвет акцента</label>
              <input
                type="color" value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                className="h-9 w-12 border border-surface-700 bg-surface-800 rounded cursor-pointer"
              />
            </div>
            <div className="flex-1">
              <label className="block text-surface-300 text-xs font-medium mb-1.5">Icon URL</label>
              <input
                value={iconUrl} onChange={(e) => setIconUrl(e.target.value)}
                className="irc-input" placeholder="https://…/icon.png"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button type="submit" className="btn-primary inline-flex items-center gap-2">
              <Save size={14} /> Сохранить
            </button>
          </div>
        </form>
      </section>

      {/* Members ── */}
      <section className="bg-surface-900 border border-surface-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-surface-200 mb-4">Участники</h2>
        {memberError && (
          <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2 mb-3">
            {memberError}
          </div>
        )}
        <form onSubmit={onAddMember} className="flex gap-2 mb-4 items-start">
          <div className="flex-1">
            <UserPicker
              value={newMemberPicked}
              onChange={setNewMemberPicked}
              excludeUserIds={existingMemberIds}
              placeholder="Имя или @username…"
            />
          </div>
          <select
            value={newMemberRole} onChange={(e) => setNewMemberRole(e.target.value as 'ADMIN' | 'MEMBER')}
            className="irc-input w-28 flex-shrink-0"
          >
            <option value="MEMBER">MEMBER</option>
            <option value="ADMIN">ADMIN</option>
          </select>
          <button type="submit" className="btn-primary inline-flex items-center gap-1 flex-shrink-0">
            <UserPlus size={14} /> Добавить
          </button>
        </form>
        <div className="border-t border-surface-800 pt-3">
          {(!members || members.length === 0) ? (
            <p className="text-surface-500 text-sm">Нет участников.</p>
          ) : (
            <ul className="divide-y divide-surface-800/60">
              {members.map((m) => (
                <li key={m.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-surface-100 text-sm">{m.user?.displayName ?? m.userId}</p>
                    <p className="text-surface-500 text-xs">@{m.user?.username ?? '—'} · {m.role}</p>
                  </div>
                  {m.role !== 'OWNER' && (
                    <button
                      onClick={() => onRemoveMember(m.userId, m.user?.username)}
                      className="btn-ghost text-xs px-2 py-1 text-red-400"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <TotpModal
        open={totpOpen}
        onClose={() => { setTotpOpen(false); pendingOpRef[1](null); setTotpError({ code: null, message: null }); }}
        onSubmit={handleTotpSubmit}
        errorCode={totpError.code}
        errorMessage={totpError.message}
        loading={totpLoading}
        actionLabel={`Изменение workspace «${ws.slug}»`}
      />
    </div>
  );
}
